<template>
  <BaseLoader v-if="!$store.getters.isLoaded(['catalog', 'products', 'shelves'])" />
  <div v-else>
    <div class="d-flex justify-content-between align-items-center mt-3 mb-3">
      <h1 class="h2">Missing Products</h1>
      <div>
        <button class="btn btn-sm btn-outline-danger mr-2" @click="resetPurchase" v-if="productsInCartCount > 0">
          Reset {{ productsInCartCount }} {{ productsInCartCount > 1 ? 'Purchases' : 'Purchase' }}
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="showSettingsLayer = true">Settings</button>
      </div>
    </div>
    <p class="alert alert-success" v-if="itemsBySource === null">There are no Missing Items</p>
    <template v-else>
      <ul class="list-group mb-4" v-for="(categories, sourceKey) in itemsBySource" :key="sourceKey">
        <li class="list-group-item list-group-item-primary pt-2 pb-2" v-if="sources">
          <span v-if="sourceKey === 'All Sources'">{{ sourceKey }}</span>
          <span v-else>{{ sources.find(s => s.id === sourceKey).label }}</span>
        </li>
        <div v-for="category in categories" :key="category.categoryId">
          <li class="list-group-item list-group-item-secondary pt-1 pb-1">
            <small>{{ category.categoryLabel }}</small>
          </li>
          <li
            v-for="item in category.items"
            :key="item.productId"
            class="list-group-item d-flex flex-column align-items-end flex-md-row align-items-md-center"
            @click="setPurchase(item.productId)"
          >
            <div class="flex-grow-1 d-flex align-items-center w-100">
              <span
                class="missing-products__img flex-shrink-0  mr-3"
                :style="
                  `background-image: url(https://storage.googleapis.com/emma-static/products/${item.img}_256.jpg)`
                "
              />
              <div class="flex-grow-1">
                <div>
                  <span>{{ item.brandName }} {{ item.label }}</span>
                  <span class="badge badge-pill badge-danger ml-2" v-if="item.status.discontinued">Discontinued</span>
                  <span class="badge badge-pill badge-warning ml-2" v-if="item.temporarilyUnavailable"
                    >Temp. Unavailable</span
                  >
                </div>
                <small v-if="item.additionalLabel">{{ item.additionalLabel }}, </small>
                <small>{{ item.packageSize }} {{ item.packageUnit }}, {{ item.productId }}</small>
              </div>
            </div>
            <div style="font-size: 1.125rem" class="text-nowrap">
              <div class="badge badge-secondary badge-pill mr-2">📦 {{ item.inStock }} / {{ item.rebuyAmount }}</div>
              <div
                class="badge badge-pill mr-2"
                style="width: 4rem;"
                :class="item.demandFromOrders <= item.inCart ? 'badge-success' : 'badge-warning'"
              >
                🧾 {{ item.demandFromOrders }}
              </div>
              <div
                class="badge badge-pill"
                :class="{
                  'badge-light': item.inCart === 0,
                  'badge-secondary': item.inCart > 0,
                  'badge-success': item.inCart >= item.demandFromOrders
                }"
                style="width: 4rem;"
              >
                🛒 {{ item.inCart }}
              </div>
            </div>
          </li>
        </div>
      </ul>
    </template>

    <Layer v-if="showSettingsLayer" title="Settings" @close="showSettingsLayer = false" padding="2rem 1rem">
      <div class="mb-4">
        <label class="d-block font-weight-bold ml-2">Source:</label>
        <select class="custom-select" v-model="filters.source">
          <option :value="null">All</option>
          <option v-for="source in sources" :key="source.id" :value="source.id">
            {{ source.label }}
          </option>
        </select>
      </div>

      <div class="custom-control custom-switch mb-2">
        <input
          type="checkbox"
          class="custom-control-input"
          id="includeOutOfStock"
          v-model="filters.includeOutOfStock"
        />
        <label class="custom-control-label" for="includeOutOfStock">Include Out of Stock Items</label>
      </div>
      <div class="custom-control custom-switch mb-2">
        <input
          type="checkbox"
          class="custom-control-input"
          id="includeTemporarilyUnavailable"
          v-model="filters.includeTemporarilyUnavailable"
        />
        <label class="custom-control-label" for="includeTemporarilyUnavailable">Include Temporarily Unavailable</label>
      </div>
      <div class="custom-control custom-switch mb-2">
        <input
          type="checkbox"
          class="custom-control-input"
          id="includeDiscontinuedProducts"
          v-model="filters.includeDiscontinuedProducts"
        />
        <label class="custom-control-label" for="includeDiscontinuedProducts">Include Discontinued Products</label>
      </div>
      <div class="custom-control custom-switch mb-2">
        <input type="checkbox" class="custom-control-input" id="showAsOneSource" v-model="filters.showAsOneSource" />
        <label class="custom-control-label" for="showAsOneSource">Show as one Source</label>
      </div>
    </Layer>
  </div>
</template>

<script>
import { db } from '@/shared/firebase'
import { ItemsInStock, MissingItems } from '@/helpers/Calculator'
import Layer from '@/shared/Layer'

export default {
  components: { Layer },
  data() {
    return {
      filters: {
        source: null,
        includeOutOfStock: false,
        includeTemporarilyUnavailable: true,
        includeDiscontinuedProducts: false,
        showAsOneSource: false
      },
      productsInCart: {},
      purchaseSubscription: null,
      showSettingsLayer: false,
      source: null,
      sources: null
    }
  },
  created() {
    this.purchaseSubscription = db.doc(`private/purchase`).onSnapshot(doc => {
      const { productsInCart, sources } = doc.data()
      this.sources = _.orderBy(
        Object.keys(sources).map(id => {
          return { ...sources[id], id }
        }),
        [source => source.label.toLowerCase()]
      )
      this.productsInCart = productsInCart || {}
    })
  },
  beforeDestroy() {
    this.purchaseSubscription()
  },
  computed: {
    itemsBySource() {
      let { orders, shelves, catalog, products } = this.$store.state
      const flatCategories = getFlatCategories(catalog)
      let missingFromOrders = MissingItems(
        orders.filter(o => !o.orderJobId),
        shelves
      )
      let missingItems = []
      const itemsInStock = ItemsInStock(shelves)
      if (!this.filters.includeDiscontinuedProducts) products = products.filter(p => !p.status.discontinued)
      if (!this.filters.includeTemporarilyUnavailable) products = products.filter(p => !p.status.temporarilyUnavailable)
      products.forEach(product => {
        if (product.source === undefined) product.source = 'edeka-foodservice'
        if (product.sourceLabel === undefined) product.sourceLabel = 'EDEKA Foodservice'
        const stockCount = itemsInStock[product.id] || 0
        const demandFromOrders = missingFromOrders[product.id] || 0
        const demandfromRebuyAmount = stockCount < product.rebuyAmount ? product.rebuyAmount : 0
        if (demandFromOrders || (this.filters.includeOutOfStock && demandfromRebuyAmount)) {
          missingItems.push({
            ...product,
            demandFromOrders,
            demandfromRebuyAmount,
            productId: product.id,
            inStock: stockCount
          })
        }
      })
      if (missingItems.length === 0) return null
      if (this.filters.showAsOneSource) {
        missingItems = missingItems.map(item => {
          item.source = 'All Sources'
          return item
        })
      }
      let itemsBySource = _.groupBy(missingItems, 'source')
      if (this.filters.source) {
        itemsBySource = {
          [this.filters.source]: itemsBySource[this.filters.source]
        }
      }
      const sources = {}
      Object.keys(itemsBySource)
        .sort()
        .forEach(sourceKey => {
          const categories = _.groupBy(itemsBySource[sourceKey], 'categoryId')
          sources[sourceKey] = Object.keys(categories)
            .sort()
            .map(categoryId => {
              const items = categories[categoryId].map(item => {
                item.inCart = this.productsInCart[item.productId]?.amount || 0
                return item
              })
              return {
                categoryId,
                categoryLabel: flatCategories.find(c => c.id === categoryId).label,
                items
              }
            })
        })
      return sources
    },
    productsInCartCount() {
      return Object.keys(this.productsInCart).length
    }
  },
  methods: {
    setPurchase(productId) {
      const product = this.$store.state.products.find(p => p.id === productId)
      const amount = prompt(`Enter purchase amount for ${product.brandName} ${product.label}`)
      if (amount && !isNaN(amount)) {
        db.doc('private/purchase').update({
          [`productsInCart.${productId}`]: {
            amount
          }
        })
      }
    },
    resetPurchase() {
      const confirmed = confirm(`Are you sure to reset ${this.productsInCartCount} items?`)
      if (confirmed) {
        db.doc(`private/purchase`)
          .update({
            productsInCart: {}
          })
          .then(() => {
            this.productsInCart = {}
          })
      }
    }
  }
}

const getFlatCategories = catalog => {
  const categories = []
  const getCategories = catalog => {
    Object.keys(catalog.categories).forEach(categoryId => {
      const category = catalog.categories[categoryId]
      categories.push({
        label: category.label,
        id: categoryId,
        product: category.products
      })
      if (category.categories) getCategories({ categories: category.categories })
    })
  }
  getCategories(catalog)
  return _.orderBy(categories, ['id'])
}
</script>

<style lang="scss" scoped>
.missing-products {
  &__img {
    width: 4rem;
    height: 4rem;
    background-position: center center;
    background-size: cover;
    display: inline-block;
  }
}
</style>
